import { useState } from 'react';
import { mockAttendanceHistory } from '../../../mocks/attendance';

export default function AttendanceHistory() {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedPersonal, setSelectedPersonal] = useState('all');

  const filteredHistory = mockAttendanceHistory.filter(record => {
    const dateMatch = record.date === selectedDate;
    const personalMatch = selectedPersonal === 'all' || record.personalId === selectedPersonal;
    return dateMatch && personalMatch;
  });

  const calculateTotalHours = () => {
    return filteredHistory.reduce((total, record) => {
      if (!record.checkOut) return total;
      const [inHour, inMin] = record.checkIn.split(':').map(Number);
      const [outHour, outMin] = record.checkOut.split(':').map(Number);
      const minutes = (outHour * 60 + outMin) - (inHour * 60 + inMin);
      return total + minutes;
    }, 0);
  };

  const totalMinutes = calculateTotalHours();
  const totalHours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;

  const calculateHours = (checkIn: string, checkOut: string | null) => {
    if (!checkOut) return 'En turno';
    
    const [inHour, inMin] = checkIn.split(':').map(Number);
    const [outHour, outMin] = checkOut.split(':').map(Number);
    
    const totalMinutes = (outHour * 60 + outMin) - (inHour * 60 + inMin);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    return `${hours}h ${minutes}m`;
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-amber-500 to-amber-700 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <i className="ri-time-line text-4xl opacity-80"></i>
            <span className="text-sm font-semibold bg-white/20 px-3 py-1 rounded-full">Hoy</span>
          </div>
          <p className="text-amber-100 text-sm mb-1">Total de Horas</p>
          <p className="text-3xl font-bold">{totalHours}h {remainingMinutes}m</p>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-700 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <i className="ri-user-follow-line text-4xl opacity-80"></i>
            <span className="text-sm font-semibold bg-white/20 px-3 py-1 rounded-full">Activos</span>
          </div>
          <p className="text-green-100 text-sm mb-1">Personal Trabajando</p>
          <p className="text-3xl font-bold">{filteredHistory.filter(r => !r.checkOut).length}</p>
        </div>

        <div className="bg-gradient-to-br from-gray-500 to-gray-700 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <i className="ri-checkbox-circle-line text-4xl opacity-80"></i>
            <span className="text-sm font-semibold bg-white/20 px-3 py-1 rounded-full">Completados</span>
          </div>
          <p className="text-gray-100 text-sm mb-1">Turnos Finalizados</p>
          <p className="text-3xl font-bold">{filteredHistory.filter(r => r.checkOut).length}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6">Filtros de Búsqueda</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Fecha
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all cursor-pointer"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Personal
            </label>
            <select
              value={selectedPersonal}
              onChange={(e) => setSelectedPersonal(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all cursor-pointer"
            >
              <option value="all">Todos</option>
              <option value="1">Carlos Rodríguez</option>
              <option value="2">María González</option>
              <option value="3">Juan Martínez</option>
              <option value="4">Ana López</option>
              <option value="5">Pedro Sánchez</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">Personal</th>
                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">Fecha</th>
                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">Entrada</th>
                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">Salida</th>
                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">Horas</th>
                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredHistory.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <i className="ri-calendar-close-line text-6xl text-gray-300 mb-4"></i>
                    <p className="text-gray-500 text-lg">No hay registros para esta fecha</p>
                  </td>
                </tr>
              ) : (
                filteredHistory.map(record => (
                  <tr key={record.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-700 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-sm">
                            {record.personalName.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                        <span className="font-semibold text-gray-900">{record.personalName}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {new Date(record.date).toLocaleDateString('es-ES', { 
                        day: '2-digit', 
                        month: 'short', 
                        year: 'numeric' 
                      })}
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-2 text-gray-900 font-semibold">
                        <i className="ri-login-box-line text-green-600"></i>
                        {record.checkIn}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {record.checkOut ? (
                        <span className="inline-flex items-center gap-2 text-gray-900 font-semibold">
                          <i className="ri-logout-box-line text-red-600"></i>
                          {record.checkOut}
                        </span>
                      ) : (
                        <span className="text-gray-400 italic">En turno</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-bold text-gray-900">
                        {calculateHours(record.checkIn, record.checkOut)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                        record.checkOut
                          ? 'bg-gray-100 text-gray-700'
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {record.checkOut ? 'Completado' : 'En turno'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-gradient-to-br from-amber-50 to-white rounded-xl p-6 border border-amber-100">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-lg font-bold text-gray-900 mb-1">Exportar Reporte</h4>
            <p className="text-gray-600 text-sm">Descarga el historial de asistencia en formato Excel</p>
          </div>
          <button className="px-6 py-3 bg-amber-700 text-white rounded-lg font-semibold hover:bg-amber-800 transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer">
            <i className="ri-file-excel-line mr-2"></i>
            Descargar Excel
          </button>
        </div>
      </div>
    </div>
  );
}
